import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router";
import type { ReviewItem, Task } from "@nelurio/contracts";
import { Badge, Button, Card, EmptyState, Icon, PageHeader } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { api, isDemoMode } from "../api/client";
import { demoDashboard } from "../demo/data";
import { notifyDataChanged, useCourses } from "../hooks/useCourses";
import { TaskComposer } from "../components/TaskComposer";
import { fetchRoutines, removeRoutine, routinesForDate, toggleRoutineDoneApi, type Routine } from "../lib/routines";
import { categoryLabel } from "../lib/categories";
import { buildOverdueList, daysLate, nextDayKey } from "../lib/overdue";
import { hasDetails, normalizeDescription, toggleExpanded } from "../lib/taskDetails";
import { parseDateKey, toDateKey } from "../lib/dateNav";

function localDateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function TasksPage() {
  const { courses } = useCourses();
  const { t, formatDate, formatNumber } = useI18n();
  const [day, setDay] = useState(() => localDateKey(new Date()));
  const [tasks, setTasks] = useState<Task[]>([]);
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingRoutine, setEditingRoutine] = useState<Routine | null>(null);
  const [editing, setEditing] = useState<Task | null>(null);
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [doneIds, setDoneIds] = useState<Set<string>>(() => new Set());
  // Reviews keep journal semantics: checked stays listed (strikethrough).
  // Server is the source of truth (completedAt); the in-session set covers the
  // optimistic tick before the next fetch.
  const [reviewsDone, setReviewsDone] = useState<Set<string>>(() => new Set());
  const isReviewDone = (review: ReviewItem) => Boolean(review.completedAt) || reviewsDone.has(review.id);
  const [error, setError] = useState("");

  const shiftDay = (days: number) => {
    const [y, m, d] = day.split("-").map(Number);
    const next = new Date(y, m - 1, d + days);
    setDay(localDateKey(next));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (isDemoMode()) {
        setTasks(demoDashboard.scheduled ?? []);
        setReviews(demoDashboard.reviews ?? []);
      } else {
        const [taskItems, reviewItems] = await Promise.all([
          api<Task[]>(`/tasks?date=${day}`),
          api<{ reviews: ReviewItem[] }>(`/dashboard?date=${day}`).then((data) => data.reviews ?? []).catch(() => [])
        ]);
        setTasks(Array.isArray(taskItems) ? taskItems : []);
        setReviews(Array.isArray(reviewItems) ? reviewItems : []);
      }
    } catch {
      setError(t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [day, t]);

  useEffect(() => { void load(); }, [load]);

  // Routines come from the server (same list on every device), refreshed on
  // day change, on data events and on window focus (cross-device sync).
  const loadRoutines = useCallback(async () => {
    try {
      const data = await fetchRoutines(day);
      setRoutines(data.routines);
      setDoneIds(new Set(data.done));
    } catch { setRoutines([]); }
  }, [day]);
  useEffect(() => { void loadRoutines(); }, [loadRoutines]);
  useEffect(() => {
    const refetch = () => void loadRoutines();
    window.addEventListener("nelurio:data-changed", refetch);
    window.addEventListener("focus", refetch);
    return () => { window.removeEventListener("nelurio:data-changed", refetch); window.removeEventListener("focus", refetch); };
  }, [loadRoutines]);

  // The mobile nav FAB deep-links here with ?new=1 to open the composer.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setEditing(null);
      setComposerOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [y, m, d] = day.split("-").map(Number);
  const dateForRoutines = useMemo(() => new Date(y, m - 1, d), [y, m, d]);
  const dayRoutines = useMemo(() => routinesForDate(routines, dateForRoutines), [routines, dateForRoutines]);
  const doneForDay = doneIds;

  // Unified todo definition (same on dashboard stat & TodayPlan): the user's
  // own tasks only — exams, classes and auto-created lessons live elsewhere.
  const todos = useMemo(() => tasks.filter((task) => task.kind !== "exam" && task.kind !== "class" && task.kind !== "new_lesson"), [tasks]);
  const exams = useMemo(() => tasks.filter((task) => task.kind === "exam"), [tasks]);
  // A routine IS a task: it also appears in the todo boxes (with badges) and
  // checking either copy toggles the same shared done-state.
  const scheduled = useMemo(() => [
    ...todos.filter((task) => task.dueTime).map((task) => ({ type: "task" as const, task })),
    ...dayRoutines.filter((routine) => routine.time).map((routine) => ({ type: "routine" as const, routine }))
  ], [todos, dayRoutines]);
  const anytime = useMemo(() => [
    ...todos.filter((task) => !task.dueTime).map((task) => ({ type: "task" as const, task })),
    ...dayRoutines.filter((routine) => !routine.time).map((routine) => ({ type: "routine" as const, routine }))
  ], [todos, dayRoutines]);
  // Priority tasks float to the top of their box (server already orders too).
  const sortEntries = (list: Array<{ type: "task"; task: Task } | { type: "routine"; routine: Routine }>) =>
    [...list].sort((a, b) => {
      const pa = a.type === "task" ? (a.task as Task & { priorityNum?: number }).priorityNum ?? 0 : 0;
      const pb = b.type === "task" ? (b.task as Task & { priorityNum?: number }).priorityNum ?? 0 : 0;
      return pb - pa;
    });

  const completeTask = async (task: Task) => {
    const nextDone = task.status !== "completed";
    // Journal semantics: flip status in place, the row stays listed.
    setTasks((current) => current.map((item) => (item.id === task.id ? { ...item, status: nextDone ? "completed" : "open" } : item)));
    if (!isDemoMode()) {
      try {
        await api(`/tasks/${task.id}`, { method: "PATCH", body: JSON.stringify({ status: nextDone ? "completed" : "open" }) });
        notifyDataChanged();
      } catch {
        load();
      }
    }
  };

  const deleteTask = async (task: Task) => {
    setTasks((current) => current.filter((item) => item.id !== task.id));
    if (!isDemoMode()) {
      try {
        await api(`/tasks/${task.id}`, { method: "DELETE" });
        notifyDataChanged();
      } catch {
        load();
      }
    }
  };

  const moveOverdueDelete = async (task: Task) => {
    setOverdue((current) => current.filter((item) => item.id !== task.id));
    try {
      await api(`/tasks/${task.id}`, { method: "DELETE" });
      notifyDataChanged();
    } catch {
      void loadOverdue();
    }
  };
  const moveOverdue = async (task: Task, dayKey: string) => {
    setOverdue((current) => current.filter((item) => item.id !== task.id));
    try {
      await api(`/tasks/${task.id}`, { method: "PATCH", body: JSON.stringify({ dueDate: dayKey }) });
      notifyDataChanged();
      if (dayKey === day) void load();
    } catch {
      void loadOverdue();
    }
  };

  const completeReview = async (review: ReviewItem, difficulty: string) => {
    setReviewsDone((current) => new Set(current).add(review.id));
    if (!isDemoMode()) {
      try {
        await api(`/reviews/${review.id}/complete`, { method: "POST", body: JSON.stringify({ difficulty }) });
        notifyDataChanged();
      } catch {
        // Roll the optimistic check back instead of notifying + reloading over it.
        setReviewsDone((current) => {
          const next = new Set(current);
          next.delete(review.id);
          return next;
        });
      }
    }
  };

  // Efficiency = share of today's items (todos + routines + reviews) that are done.
  const totalItems = todos.length + dayRoutines.length + reviews.length;
  const doneItems = todos.filter((task) => task.status === "completed").length
    + dayRoutines.filter((routine) => doneForDay.has(routine.id)).length
    + reviews.filter((review) => isReviewDone(review)).length;
  const backlogCount = courses.reduce((sum, course) => sum + (course.dueReviewCount || 0), 0);
  const leftToday = Math.max(0, totalItems - doneItems);


  const toggleRoutine = async (routine: Routine) => {
    const nextDone = !doneForDay.has(routine.id);
    setDoneIds((current) => {
      const next = new Set(current);
      if (nextDone) next.add(routine.id);
      else next.delete(routine.id);
      return next;
    });
    try {
      await toggleRoutineDoneApi(routine.id, day, nextDone);
    } catch {
      // Roll back so the checkmark matches the server on failure.
      setDoneIds((current) => {
        const next = new Set(current);
        if (nextDone) next.delete(routine.id);
        else next.add(routine.id);
        return next;
      });
    }
  };

  const deleteRoutine = async (routine: Routine) => {
    setRoutines((current) => current.filter((item) => item.id !== routine.id));
    await removeRoutine(routine.id).catch(() => undefined);
  };

  const onComposerSaved = (task: Task | null, routine?: Routine) => {
    if (routine) {
      void loadRoutines();
      return;
    }
    if (!task) return;
    setTasks((current) => {
      const exists = current.some((item) => item.id === task.id);
      return exists ? current.map((item) => (item.id === task.id ? task : item)) : task.dueDate === day ? [task, ...current] : current;
    });
    if (task.dueDate !== day) void load();
  };

  const openCreate = () => { setEditing(null); setEditingRoutine(null); setComposerOpen(true); };
  const openEdit = (task: Task) => { setEditing(task); setComposerOpen(true); };

  const dayLabel = formatDate(`${day}T12:00:00`, { weekday: "long", month: "long", day: "numeric" });
  const todayKey = localDateKey(new Date());
  const isToday = day === todayKey;
  // Yesterday and older are history: view-only, efficiency stays recorded.
  const isPast = day < todayKey;

  // Task-debt pipeline: OPEN work whose date passed, surfaced for a verdict —
  // settle it (done), move it to another day, or delete it. Never silent rot.
  const [overdue, setOverdue] = useState<Task[]>([]);
  const loadOverdue = useCallback(async () => {
    if (isDemoMode()) { setOverdue([]); return; }
    try {
      const items = await api<Task[]>("/tasks/overdue");
      setOverdue(Array.isArray(items) ? items : []);
    } catch { setOverdue([]); }
  }, []);
  useEffect(() => { void loadOverdue(); }, [loadOverdue]);
  useEffect(() => {
    const refetch = () => void loadOverdue();
    window.addEventListener("nelurio:data-changed", refetch);
    return () => window.removeEventListener("nelurio:data-changed", refetch);
  }, [loadOverdue]);
  const overdueList = useMemo(() => buildOverdueList(overdue, todayKey) as Task[], [overdue, todayKey]);
  const [expandedDetails, setExpandedDetails] = useState<Set<string>>(() => new Set());
  const [jumpOpen, setJumpOpen] = useState(false);
  const [overdueOpen, setOverdueOpen] = useState(false);

  const settleOverdue = async (task: Task) => {
    setOverdue((current) => current.filter((item) => item.id !== task.id));
    try {
      await api(`/tasks/${task.id}`, { method: "PATCH", body: JSON.stringify({ status: "completed" }) });
      notifyDataChanged();
    } catch {
      void loadOverdue();
    }
  };

  const entryRow = (entry: { type: "task"; task: Task } | { type: "routine"; routine: Routine }) => {
    if (entry.type === "routine") {
      const routine = entry.routine;
      const done = doneForDay.has(routine.id);
      return (
        <article className={`simple-task routine-inline ${done ? "is-done" : ""}`} key={`r-${routine.id}`}>
          <button className={`simple-task__check ${done ? "is-checked" : ""}`} disabled={isPast} aria-label={t("common.completed")} onClick={() => toggleRoutine(routine)}><Icon name="check" size={14} /></button>
          <div><strong>{routine.title}</strong><small>{categoryLabel(routine.kind, t)}</small></div>
          <span className="entry-badges">
            <Badge tone="blue">{t("routine.title")}</Badge>
            {routine.time && <Badge tone="purple"><Icon name="clock" size={11} />{routine.time}</Badge>}
          </span>
          <time>{routine.time || t("common.noTime")}</time>
          {/* routine rows keep BOTH actions on past days too: editing changes the
              recurring TEMPLATE (safe), only the check is history-locked */}
          <span className="simple-task__actions">
            <button onClick={() => { setEditingRoutine(routine); setComposerOpen(true); }} aria-label={t("common.edit")} title={t("common.edit")}><Icon name="edit" size={14} /></button>
            <button onClick={() => deleteRoutine(routine)} aria-label={t("common.delete")} title={t("common.delete")}><Icon name="trash" size={14} /></button>
          </span>
        </article>
      );
    }
    const task = entry.task;
    const priorityNum = (task as Task & { priorityNum?: number }).priorityNum ?? 0;
    const isDone = task.status === "completed";
    return (
      <article className={`simple-task ${isDone ? "is-done" : ""}`} key={task.id}>
        <button className={`simple-task__check ${isDone ? "is-checked" : ""}`} disabled={isPast} aria-label={t("common.completed")} onClick={() => void completeTask(task)}><Icon name="check" size={14} /></button>
        <div className="simple-task__body">
          {hasDetails(task) ? (
            <button type="button" className="task-details-toggle" aria-expanded={expandedDetails.has(task.id)} title={t("task.details")} onClick={() => setExpandedDetails((ids) => toggleExpanded(ids, task.id))}>
              <strong>{task.title}</strong><small>{categoryLabel(task.kind, t)}{task.courseTitle ? ` · ${task.courseTitle}` : ""}</small>
            </button>
          ) : (
            <><strong>{task.title}</strong><small>{categoryLabel(task.kind, t)}{task.courseTitle ? ` · ${task.courseTitle}` : ""}</small></>
          )}
          {expandedDetails.has(task.id) && <p className="task-desc">{normalizeDescription(task.description)}</p>}
        </div>
        {priorityNum > 0 && <span className="priority-badge" title={t("task.priorityLabel")}>{"★".repeat(Math.min(3, priorityNum))} {priorityNum}</span>}
        <time>{task.dueTime || t("common.noTime")}</time>
        {!isPast && (
          <span className="simple-task__actions">
            <button onClick={() => openEdit(task)} aria-label={t("common.edit")}><Icon name="edit" size={14} /></button>
            <button onClick={() => void deleteTask(task)} aria-label={t("common.delete")}><Icon name="trash" size={14} /></button>
          </span>
        )}
      </article>
    );
  };

  return (
    <div className="page-stack rise">
      <PageHeader title={t("nav.tasks")} subtitle={t("tasks.subtitle")} />

      {overdueList.length > 0 && (
        <button type="button" className="overdue-entry" onClick={() => setOverdueOpen(true)} aria-haspopup="dialog">
          <Icon name="warning" size={16} />
          <strong>{t("overdue.sectionTitle")}</strong>
          <small>{t("overdue.lead")}</small>
          <Badge tone="amber">{formatNumber(overdueList.length)}</Badge>
          <Icon name="chevron" size={14} />
        </button>
      )}

      <div className="date-navigator" role="group" aria-label={t("tasks.pickDay")}>
        <Button variant="secondary" icon="chevron" className="date-navigator__prev" onClick={() => shiftDay(-1)} aria-label={t("tasks.prevDay")} />
        <button type="button" className="date-navigator__label date-navigator__label--button" aria-haspopup="dialog" aria-expanded={jumpOpen} title={t("tasks.jumpToDate")} onClick={() => setJumpOpen((open) => !open)}>
          <strong>{dayLabel}</strong>
          {isToday && <Badge tone="purple">{t("common.today")}</Badge>}
        </button>
        <Button variant="secondary" icon="chevron" className="date-navigator__next" onClick={() => shiftDay(1)} aria-label={t("tasks.nextDay")} />
      </div>

      {jumpOpen && (
        <div className="date-jump" role="dialog" aria-label={t("tasks.jumpToDate")}>
          <label className="nl-field">
            <span className="nl-field__label">{t("tasks.jumpToDate")}</span>
            <input
              type="date"
              className="nl-input"
              value={day}
              autoFocus
              onChange={(event) => {
                const key = event.target.value;
                const parsed = parseDateKey(key);
                if (!parsed) return;
                setDay(toDateKey(parsed));
                setJumpOpen(false);
              }}
            />
          </label>
        </div>
      )}

      {/* Primary action directly under the date bar, full width on every layout. */}
      <Button icon="plus" className="tasks-new-cta" onClick={openCreate}>{t("task.new")}</Button>

      {overdueOpen && createPortal(
        <div className="composer-backdrop" onClick={() => setOverdueOpen(false)} role="presentation">
          <div className="composer-modal rise overdue-modal" role="dialog" aria-modal="true" aria-label={t("overdue.sectionTitle")} onClick={(event) => event.stopPropagation()}>
            <div className="composer-modal__head">
              <h2>{t("overdue.sectionTitle")}</h2>
              <Badge tone="amber">{formatNumber(overdueList.length)}</Badge>
              <button className="composer-modal__close" onClick={() => setOverdueOpen(false)} aria-label={t("common.cancel")}><Icon name="x" size={16} /></button>
            </div>
            <p className="overdue-modal__lead">{t("overdue.lead")}</p>
            <div className="overdue-modal__list">
              {overdueList.map((task) => (
                <article className="overdue-modal__item" key={task.id}>
                  <div className="overdue-modal__copy">
                    <strong>{task.title}</strong>
                    <small>
                      {formatDate(`${task.dueDate}T12:00:00`, { day: "numeric", month: "short" })}
                      {task.dueTime ? ` · ${task.dueTime}` : ""}
                      {task.courseTitle ? ` · ${task.courseTitle}` : ""}
                      {" · "}
                      {t("overdue.daysLate", { count: formatNumber(daysLate(task.dueDate, todayKey)) })}
                    </small>
                  </div>
                  <div className="overdue-modal__actions">
                    <Button variant="secondary" icon="check" onClick={() => void settleOverdue(task)}>{t("overdue.settleDone")}</Button>
                    <Button variant="secondary" icon="arrow" onClick={() => void moveOverdue(task, nextDayKey(todayKey))}>{t("overdue.moveTomorrow")}</Button>
                    <input
                      type="date"
                      className="nl-input overdue-modal__date"
                      aria-label={t("overdue.moveTo")}
                      min={todayKey}
                      onChange={(event) => { if (event.target.value) void moveOverdue(task, event.target.value); }}
                    />
                    <Button variant="secondary" icon="trash" onClick={() => void moveOverdueDelete(task)}>{t("common.delete")}</Button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      <TaskComposer open={composerOpen} dateKey={day} task={editing} routine={editingRoutine} onClose={() => { setComposerOpen(false); setEditing(null); setEditingRoutine(null); }} onSaved={onComposerSaved} />

      {/* ── Three horizontal metric rings: today at a glance ── */}
      <div className="task-rings">
        {[
          { key: "leftToday", label: t("tasks.ringLeftToday"), value: totalItems ? (doneItems / totalItems) * 100 : 0, tone: "var(--nl-green)", display: formatNumber(leftToday) },
          { key: "backlog", label: t("tasks.ringBacklog"), value: backlogCount > 0 ? 100 : 0, tone: backlogCount > 0 ? "var(--nl-danger)" : "var(--nl-purple)", display: formatNumber(backlogCount) },
          { key: "routines", label: t("tasks.ringRoutines"), value: dayRoutines.length ? Math.round((dayRoutines.filter((r) => doneForDay.has(r.id)).length / dayRoutines.length) * 100) : 0, tone: "var(--nl-blue)", display: formatNumber(dayRoutines.length) }

        ].map((ring) => (
          <Card className="task-ring" key={ring.key}>
            <span className="task-ring__circle" style={{ "--progress": `${ring.value * 3.6}deg`, "--ring-tone": ring.tone } as React.CSSProperties}>
              <strong>{ring.display}</strong>
            </span>
            <small>{ring.label}</small>
          </Card>
        ))}
      </div>

      {/* ── Routines & reviews of the day ── */}
      <div className="day-columns">
        <Card className="task-column">
          <div className="section-heading compact"><div><span className="section-kicker">{t("routine.title")}</span><h2>{t("routine.subtitle")}</h2></div><Badge tone="blue">{formatNumber(dayRoutines.length)}</Badge></div>
          {dayRoutines.map((routine) => {
            const done = doneForDay.has(routine.id);
            return (
              <article className={`simple-task routine-task ${done ? "is-done" : ""}`} key={routine.id}>
                <button className={`simple-task__check ${done ? "is-checked" : ""}`} aria-label={t("common.completed")} onClick={() => toggleRoutine(routine)}><Icon name="check" size={14} /></button>
                <div><strong>{routine.title}</strong><small>{categoryLabel(routine.kind, t)}</small></div>
                <time>{routine.time || t("common.noTime")}</time>
                <span className="simple-task__actions">
                  {/* edit = the recurring TEMPLATE — safe on every day */}
                  <button onClick={() => { setEditingRoutine(routine); setComposerOpen(true); }} aria-label={t("common.edit")} title={t("common.edit")}><Icon name="edit" size={14} /></button>
                  <button onClick={() => deleteRoutine(routine)} aria-label={t("common.delete")} title={t("common.delete")}><Icon name="trash" size={14} /></button>
                </span>
              </article>
            );
          })}
          {!dayRoutines.length && <p className="task-column__empty">{t("tasks.emptyRoutines")}</p>}
        </Card>
        <Card className="task-column">
          <div className="section-heading compact"><div><span className="section-kicker">{t("dashboard.dueReviews")}</span><h2>{t("reviews.title")}</h2></div><Badge tone="purple">{formatNumber(reviews.length)}</Badge></div>
          {reviews.map((review) => {
            const done = isReviewDone(review);
            return (
              <article className={`simple-task review-task ${done ? "is-done" : ""}`} key={review.id}>
                <span className="review-stage-badge" title={t(`reviews.stage${review.stage}`)}>{formatNumber(review.stage + 1)}</span>
                <div><strong>{review.title}</strong><small>{t(`reviews.stage${review.stage}`)}{review.courseTitle ? ` · ${review.courseTitle}` : ""}</small></div>
                <ReviewRecallMenu disabled={done} onPick={(difficulty) => void completeReview(review, difficulty)} />
              </article>
            );
          })}
          {!reviews.length && <p className="task-column__empty">{t("reviews.hint")}</p>}
        </Card>
      </div>


      {/* ── Divider ── */}
      <div className="tasks-divider" role="separator"><span>{t("tasks.todoDivider")}</span></div>

      {/* ── Bottom block: today's editable todo list ── */}
      <div className={`task-columns ${isPast ? "is-locked" : ""}`}>
        {(["scheduled", "anytime"] as const).map((group) => {
          const entries = sortEntries(group === "scheduled" ? scheduled : anytime);
          return (
            <Card className="task-column" key={group}>
              <div className="section-heading compact"><div><span className="section-kicker">{isToday ? t("common.today") : t("tasks.pickDay")}</span><h2>{t(group === "scheduled" ? "dashboard.scheduled" : "dashboard.anytime")}</h2></div><Badge tone="neutral">{formatNumber(entries.length)}</Badge></div>
              {entries.map(entryRow)}
              {!entries.length && <p className="task-column__empty">{t(group === "scheduled" ? "tasks.emptyScheduled" : "tasks.emptyAnytime")}</p>}
            </Card>
          );
        })}
      </div>
      {isPast && <p className="day-lock-note"><Icon name="lock" size={13} /> {t("tasks.dayLocked")}</p>}

      {exams.length > 0 && (
        <Card className="task-column">
          <div className="section-heading compact"><div><span className="section-kicker">{t("dashboard.exam")}</span><h2>{t("tasks.examsTitle")}</h2></div><Badge tone="coral">{formatNumber(exams.length)}</Badge></div>
          {exams.map((task) => entryRow({ type: "task", task }))}
        </Card>
      )}

      {!loading && !tasks.length && !reviews.length && !dayRoutines.length && !composerOpen && (
        <Card><EmptyState icon="check" title={t("tasks.emptyTitle")} description={t("tasks.emptyBody")} action={<Button icon="plus" onClick={openCreate}>{t("task.new")}</Button>} /></Card>
      )}
      {error && <div className="form-error" role="alert">{error}</div>}
    </div>
  );
}

/** Understandable recall picker: one button, a labeled dropdown instead of
 *  four cryptic glyphs. */
function ReviewRecallMenu({ disabled, onPick }: { disabled: boolean; onPick: (difficulty: string) => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", away);
    return () => window.removeEventListener("mousedown", away);
  }, [open]);

  const options = [
    { difficulty: "forgot", label: t("reviews.forgot"), hint: t("reviews.forgotHint"), tone: "#e94e49" },
    { difficulty: "hard", label: t("reviews.hard"), hint: t("reviews.hardHint"), tone: "#f4b942" },
    { difficulty: "medium", label: t("reviews.medium"), hint: t("reviews.mediumHint"), tone: "#4d8df7" },
    { difficulty: "easy", label: t("reviews.easy"), hint: t("reviews.easyHint"), tone: "#4dbd74" }
  ];

  if (disabled) return <Badge tone="green">{t("common.completed")}</Badge>;

  return (
    <div className="recall-menu" ref={boxRef}>
      <button
        className="recall-menu__trigger"
        onClick={() => {
          // Flip the popover upward when there is no room below (mobile: the
          // reviews card sits near the viewport bottom — opening down pushed
          // it behind the following cards / off-screen).
          const rect = boxRef.current?.getBoundingClientRect();
          setOpenUp(Boolean(rect) && rect!.bottom + 230 > window.innerHeight);
          setOpen((value) => !value);
        }}
        aria-expanded={open}
      >
        {t("reviews.recallButton")} <Icon name="chevron" size={13} />
      </button>
      {open && (
        <div className={`recall-menu__pop ${openUp ? "is-up" : ""}`} role="menu">
          {options.map((option) => (
            <button key={option.difficulty} role="menuitem" onClick={() => { setOpen(false); onPick(option.difficulty); }}>
              <i style={{ background: option.tone }} />
              <span className="recall-menu__copy"><strong>{option.label}</strong><small>{option.hint}</small></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
